import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getRoute, getIsochrone, decodePolyline } from '../../lib/traveltime';

// Custom icons for start and end
const createIcon = (color: string) => new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const startIcon = createIcon('green');
const endIcon = createIcon('red');
const agentIcon = createIcon('blue');

interface TravelTimeMapProps {
    center: [number, number]; // Agent/Current location
    zoom?: number;
    destination?: { lat: number; lng: number };
    origin?: { lat: number; lng: number }; // Store location
    showIsochrone?: boolean;
    travelTimeLimit?: number; // in seconds
    onRouteUpdate?: (distance: string, eta: string) => void;
}

// Helper component to update map view and fit bounds
const MapController = ({ points }: { points: [number, number][] }) => {
    const map = useMap();
    useEffect(() => {
        if (points.length > 1) {
            try {
                const bounds = L.latLngBounds(points);
                map.fitBounds(bounds, { padding: [30, 30] });
            } catch (e) {
                console.error("Bounds error:", e);
            }
        } else if (points.length === 1) {
            map.setView(points[0], 14);
        }
    }, [points, map]);
    return null;
};

const TravelTimeMap: React.FC<TravelTimeMapProps> = ({
    center,
    zoom = 13,
    destination,
    origin,
    showIsochrone = false,
    travelTimeLimit = 3600,
    onRouteUpdate
}) => {
    const [routePoints, setRoutePoints] = useState<[number, number][]>([]);
    const [isochroneCoords, setIsochroneCoords] = useState<[number, number][]>([]);

    // Points to include in bounds calculation
    const allKeyPoints = useMemo(() => {
        const pts: [number, number][] = [center];
        if (origin) pts.push([origin.lat, origin.lng]);
        if (destination) pts.push([destination.lat, destination.lng]);
        if (routePoints.length > 0) return routePoints;
        return pts;
    }, [center, origin, destination, routePoints]);

    useEffect(() => {
        const fetchRouteData = async () => {
            const startPoint = origin || { lat: center[0], lng: center[1] };
            if (destination) {
                try {
                    const routeData = await getRoute({
                        origin: startPoint,
                        destination,
                        transportation: { type: 'driving' }
                    });

                    if (routeData.results?.[0]?.locations[0]?.properties[0]) {
                        const prop = routeData.results[0].locations[0].properties[0];

                        if (prop.route) {
                            const decoded = decodePolyline(prop.route);
                            setRoutePoints(decoded);
                        } else {
                            setRoutePoints([
                                [startPoint.lat, startPoint.lng],
                                [destination.lat, destination.lng]
                            ]);
                        }

                        if (onRouteUpdate) {
                            onRouteUpdate(
                                `${(prop.distance / 1000).toFixed(1)} km`,
                                `${Math.round(prop.travel_time / 60)} min`
                            );
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch TravelTime route:', error);
                }
            }
        };

        const fetchIsochroneData = async () => {
            if (showIsochrone) {
                try {
                    const isochroneData = await getIsochrone({
                        id: 'reachability',
                        coords: { lat: center[0], lng: center[1] },
                        travelTime: travelTimeLimit,
                        transportation: { type: 'driving' },
                        departureTime: new Date().toISOString()
                    });

                    if (isochroneData.results?.[0]?.shapes) {
                        const shapes = isochroneData.results[0].shapes;
                        const coords = shapes[0].shell.map((p: any) => [p.lat, p.lng] as [number, number]);
                        setIsochroneCoords(coords);
                    }
                } catch (error) {
                    console.error('Failed to fetch TravelTime isochrone:', error);
                }
            }
        };

        fetchRouteData();
        fetchIsochroneData();
    }, [center, destination, origin, showIsochrone, travelTimeLimit]);

    return (
        <MapContainer
            center={center}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
        >
            <MapController points={allKeyPoints} />
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            <Marker position={center} icon={agentIcon}>
                <Popup>Driver Location</Popup>
            </Marker>

            {origin && (
                <Marker position={[origin.lat, origin.lng]} icon={startIcon}>
                    <Popup>Store</Popup>
                </Marker>
            )}

            {destination && (
                <Marker position={[destination.lat, destination.lng]} icon={endIcon}>
                    <Popup>Customer</Popup>
                </Marker>
            )}

            {routePoints.length > 0 && (
                <Polyline positions={routePoints} color="#10b981" weight={6} opacity={0.8} />
            )}

            {isochroneCoords.length > 0 && (
                <Polygon
                    positions={isochroneCoords}
                    pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.1, weight: 1 }}
                />
            )}
        </MapContainer>
    );
};

export default TravelTimeMap;
